import React, { useMemo } from "react";
import { Column } from "../../../../../../../../components/GeneralTable/GeneralTable";
import {
  PIVOT_COMMUNICATION_TYPE,
  Pivot,
} from "../../../../../../../../redux/pivots/types";
import moment from "moment";
import { ReactJSX } from "../../../../../../../../utils/types";
import { getModeAdd } from "../../../../../../../../utils/models/pivots";
import useConfigHistoricV5 from "../../../../../../../../hooks/models/useConfigHistoricV5";
import ExpandedContentConfigV5 from "../ExpandedContent/ExpandedContentConfigV5/ExpandedContentConfigV5";
import ConfigHistoryTable from "../ConfigHistoryTable";
import { Tooltip, Button } from "@material-ui/core";
import RemoveIcon from "@material-ui/icons/Remove";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import DoneIcon from "@material-ui/icons/Done";
import { i18n } from "../../../../../../../../i18n/i18nText";
import { Labels } from "../../../EditPivotForm/components/HistoricConfigMobile/HistoricConfigMobileV5";
import DesktopZone from "../../../../../../../../components/Zones/DesktopZone";
import { isMobile } from "../../../../../../../../mobileConfig";

const noPointer1 = {
  cursor: "default",
  marginTop: "-5px",
};

const noPointer2 = {
  cursor: "default",
  marginTop: "-7px",
};

const noPointer3 = {
  cursor: "default",
  marginTop: "-7px",
};

function getMessageStatusIcon(status: number): JSX.Element | null {
  switch (status) {
    case 0:
      return <RemoveIcon style={noPointer1} htmlColor={"rgba(0,0,0,0.6)"} />;
    case 1:
      return <DoneIcon style={noPointer2} htmlColor={"rgba(0,0,0,0.6)"} />;
    case 2:
      return <DoneAllIcon style={noPointer3} htmlColor={"rgb(33, 150, 243)"} />;
    default:
      break;
  }
}

function getMessageStatusTooltipText(status: number): string {
  switch (status) {
    case 0:
      return i18n("CONFIG_NOT_SENT");
    case 1:
      return i18n("CONFIG_SENT");
    case 2:
      return i18n("CONFIG_RECEIVED");
    default:
      break;
  }
}

interface Props {
  pivot: Pivot;
  farmID: number;
  pivotID: number;
  ctx?: any;
  pinned?: boolean;
}

const HistoricConfigV5 = (props: Props) => {
  const { pivot, pivotID, farmID } = props;
  const [
    async,
    historic,
    total,
    previousPage,
    nextPage,
    currentPage,
  ] = useConfigHistoricV5(farmID, pivotID, props.pinned);

  // MOBILE -----------------------------------------------------------------------------

  const normalColumnsMobile: Column[] = [
    {
      id: "date",
      label: <>{i18n("HISTORIC_PANEL_LABEL_DATE")}</>,
      minWidth: 170,
    },
    {
      id: "by",
      label: <>{i18n("HISTORIC_PANEL_LABEL_BY")}</>,
      minWidth: 100,
    },
  ];

  const pinnedColumnsMobile: Column[] = [
    {
      id: "name",
      label: <>{i18n("SIGN_UP_NAME")}</>,
      minWidth: 200,
    },
    {
      id: "date",
      label: <>{i18n("HISTORIC_PANEL_LABEL_DATE")}</>,
      minWidth: 170,
    },
    {
      id: "by",
      label: <>{i18n("HISTORIC_PANEL_LABEL_BY")}</>,
      minWidth: 100,
    },
  ];

  const rowsMobile = useMemo(() => {
    if (historic === undefined) return; // Historic isn't loaded yet.

    return historic.map((item) => {
      const by = (
        <span>
          {i18n("HISTORIC_CONFIG_PIVOT_DRIVE", [
            getModeAdd(item.created_on_hardware, item.created_by?.username),
          ])}
        </span>
      );

      const monitor = (
        <Tooltip title={getMessageStatusTooltipText(item.message_status)}>
          {getMessageStatusIcon(item.message_status)}
        </Tooltip>
      );

      const controller = (
        <Tooltip title={getMessageStatusTooltipText(item.message_status)}>
          {getMessageStatusIcon(item.message_status)}
        </Tooltip>
      );

      const hwsettings = (
        <Tooltip
          title={getMessageStatusTooltipText(item.device_statuses.hwsettings)}
        >
          {getMessageStatusIcon(item.device_statuses.hwsettings)}
        </Tooltip>
      );

      const gps = (
        <Tooltip title={getMessageStatusTooltipText(item.device_statuses.gps)}>
          {getMessageStatusIcon(item.device_statuses.gps)}
        </Tooltip>
      );

      const pump = (
        <Tooltip title={getMessageStatusTooltipText(item.device_statuses.pump)}>
          {getMessageStatusIcon(item.device_statuses.pump)}
        </Tooltip>
      );

      return {
        id: item.uuid,
        name: item.name ? item.name : " - ",
        date: moment(item.created).format("DD/MM/YYYY HH:mm"),
        by: by,
        datailedComponent: (): ReactJSX => (
          <ExpandedContentConfigV5
            monitor={monitor}
            controller={controller}
            hardware={hwsettings}
            gps={gps}
            pump={pump}
            pivotID={pivotID}
            farmID={farmID}
            pivot={pivot}
            pivotConfig={item}
            ctx={props.ctx}
          />
        ),
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historic]);

  // DESKTOP -----------------------------------------------------------------------------

  const normalColumnsDesktop: Column[] = [
    {
      id: "date",
      label: <>{i18n("HISTORIC_PANEL_LABEL_DATE")}</>,
      minWidth: 170,
    },
    {
      id: "by",
      label: <>{i18n("HISTORIC_PANEL_LABEL_BY")}</>,
      minWidth: 100,
    },
    {
      id: "controller",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_CONTROLLER")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_CONTROLLER")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
    {
      id: "hardware",
      label: (
        <Tooltip title={i18n("HISTORIC_CONFIG_INF_HARDWARE")}>
          <Button>{i18n("HISTORIC_PANEL_LABEL_HARDWARE")}</Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
    {
      id: "gps",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_GPS")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_GPS")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
    {
      id: "pump",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_PUMP")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_PUMP")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
  ];

  const normalColumnsDesktop4G: Column[] = [
    {
      id: "date",
      label: <>{i18n("HISTORIC_PANEL_LABEL_DATE")}</>,
      minWidth: 170,
    },
    {
      id: "by",
      label: <>{i18n("HISTORIC_PANEL_LABEL_BY")}</>,
      minWidth: 300,
    },
    {
      id: "controller",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_CONTROLLER")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_CONTROLLER")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 60,
    },
    {
      id: "hardware",
      label: (
        <Tooltip title={i18n("HISTORIC_CONFIG_INF_HARDWARE")}>
          <Button>{i18n("HISTORIC_PANEL_LABEL_HARDWARE")}</Button>
        </Tooltip>
      ),
      minWidth: 60,
    },
  ];

  const pinnedColumnsDesktop: Column[] = [
    {
      id: "name",
      label: <>{i18n("SIGN_UP_NAME")}</>,
      minWidth: 200,
    },
    {
      id: "date",
      label: <>{i18n("HISTORIC_PANEL_LABEL_DATE")}</>,
      minWidth: 170,
    },
    {
      id: "by",
      label: <>{i18n("HISTORIC_PANEL_LABEL_BY")}</>,
      minWidth: 100,
    },
    {
      id: "controller",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_CONTROLLER")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_CONTROLLER")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
    {
      id: "hardware",
      label: (
        <Tooltip title={i18n("HISTORIC_CONFIG_INF_HARDWARE")}>
          <Button>{i18n("HISTORIC_PANEL_LABEL_HARDWARE")}</Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
    {
      id: "gps",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_GPS")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_GPS")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
    {
      id: "pump",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_PUMP")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_PUMP")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    },
  ];

  const pinnedColumnsDesktop4G: Column[] = [
    {
      id: "name",
      label: <>{i18n("SIGN_UP_NAME")}</>,
      minWidth: 170,
    },
    {
      id: "date",
      label: <>{i18n("HISTORIC_PANEL_LABEL_DATE")}</>,
      minWidth: 170,
    },
    {
      id: "by",
      label: <>{i18n("HISTORIC_PANEL_LABEL_BY")}</>,
      minWidth: 240,
    },
    {
      id: "controller",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_CONTROLLER")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_CONTROLLER")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 60,
    },
    {
      id: "hardware",
      label: (
        <Tooltip title={i18n("HISTORIC_CONFIG_INF_HARDWARE")}>
          <Button>{i18n("HISTORIC_PANEL_LABEL_HARDWARE")}</Button>
        </Tooltip>
      ),
      minWidth: 60,
    },
  ];

  const retriveColumns = () => {
    if (isMobile()) {
      if (props.pinned) return pinnedColumnsMobile;
      else return normalColumnsMobile;
    } else {
      if (props.pinned) {
        if (pivot.communication_type === PIVOT_COMMUNICATION_TYPE.TYPE_4G)
          return pinnedColumnsDesktop4G;
        else return pinnedColumnsDesktop;
      } else {
        if (pivot.communication_type === PIVOT_COMMUNICATION_TYPE.TYPE_4G)
          return normalColumnsDesktop4G;
        else return normalColumnsDesktop;
      }
    }
  };

  if (pivot.automation_type == 2) {
    normalColumnsDesktop.splice(3, 1);
    normalColumnsDesktop.splice(4, 1);
    normalColumnsDesktop.splice(2, 1, {
      id: "monitor",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_MONITOR")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_MONITOR")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    });
    pinnedColumnsDesktop.splice(4, 1);
    pinnedColumnsDesktop.splice(5, 1);
    pinnedColumnsDesktop.splice(3, 1, {
      id: "monitor",
      label: (
        <Tooltip title={<>{i18n("HISTORIC_CONFIG_INF_MONITOR")}</>}>
          <Button>
            <>{i18n("HISTORIC_PANEL_LABEL_MONITOR")}</>
          </Button>
        </Tooltip>
      ),
      minWidth: 100,
    });
  }

  const rowsDesktop = useMemo(() => {
    if (historic === undefined) return; // Historic isn't loaded yet.

    return historic.map((item) => {
      const by = (
        <span>
          {i18n("HISTORIC_CONFIG_PIVOT_DRIVE", [
            getModeAdd(item.created_on_hardware, item.created_by?.username),
          ])}
        </span>
      );

      const monitor = (
        <Tooltip title={getMessageStatusTooltipText(item.message_status)}>
          {getMessageStatusIcon(item.message_status)}
        </Tooltip>
      );

      const controller = (
        <Tooltip title={getMessageStatusTooltipText(item.message_status)}>
          {getMessageStatusIcon(item.message_status)}
        </Tooltip>
      );

      const hwsettings = (
        <Tooltip
          title={getMessageStatusTooltipText(item.device_statuses.hwsettings)}
        >
          {getMessageStatusIcon(item.device_statuses.hwsettings)}
        </Tooltip>
      );

      const gps = (
        <Tooltip title={getMessageStatusTooltipText(item.device_statuses.gps)}>
          {getMessageStatusIcon(item.device_statuses.gps)}
        </Tooltip>
      );

      const pump = (
        <Tooltip title={getMessageStatusTooltipText(item.device_statuses.pump)}>
          {getMessageStatusIcon(item.device_statuses.pump)}
        </Tooltip>
      );

      if (pivot.automation_type == 2) {
        return {
          id: item.uuid,
          name: item.name ? item.name : " - ",
          date: moment(item.created).format("DD/MM/YYYY HH:mm"),
          by: by,
          monitor: monitor,
          hardware: hwsettings,
          gps: gps,
          pump: pump,
          datailedComponent: (): ReactJSX => (
            <ExpandedContentConfigV5
              pivotID={pivotID}
              farmID={farmID}
              pivot={pivot}
              pivotConfig={item}
              ctx={props.ctx}
            />
          ),
        };
      } else {
        return {
          id: item.uuid,
          name: item.name ? item.name : " - ",
          date: moment(item.created).format("DD/MM/YYYY HH:mm"),
          by: by,
          controller: controller,
          hardware: hwsettings,
          gps: gps,
          pump: pump,
          datailedComponent: (): ReactJSX => (
            <ExpandedContentConfigV5
              pivotID={pivotID}
              farmID={farmID}
              pivot={pivot}
              pivotConfig={item}
              ctx={props.ctx}
            />
          ),
        };
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historic]);

  return (
    <div>
      <DesktopZone>
        <Labels isV5 pivot={pivot} />
      </DesktopZone>

      {isMobile() ? (
        <ConfigHistoryTable
          columns={retriveColumns()}
          total={total}
          rows={rowsMobile}
          rowsPerPage={10}
          loading={
            historic === undefined || rowsMobile === undefined || async.loading
          }
          nextPage={nextPage}
          previousPage={previousPage}
          currentPage={currentPage}
        />
      ) : (
        <ConfigHistoryTable
          columns={retriveColumns()}
          total={total}
          rows={rowsDesktop}
          rowsPerPage={10}
          loading={
            historic === undefined || rowsDesktop === undefined || async.loading
          }
          nextPage={nextPage}
          previousPage={previousPage}
          currentPage={currentPage}
        />
      )}

      <DesktopZone>
        <div style={{ marginBottom: "20px" }} />
      </DesktopZone>
    </div>
  );
};

export default HistoricConfigV5;
